import React, { useState, useEffect, useCallback } from "react";
import { Alert } from "reactstrap";
import BizimhesapSettingsTab from "./BizimhesapSettingsTab";
import ParasutSettingsTab from "./ParasutSettingsTab";
import MikroSettingsTab from "./MikroSettingsTab";
import { fetchBizimhesapSettings } from "../../../api/requests/bizimhesap-requests";
import { fetchParasutSettings } from "../../../api/requests/parasut-requests";
import { fetchMikroSettings } from "../../../api/requests/mikro-requests";

import { integrationsByCategory } from "./integrations/registry";
import IntegrationSearch from "./integrations/IntegrationSearch";
import IntegrationCard from "./integrations/IntegrationCard";
import IntegrationConfigModal from "./integrations/IntegrationConfigModal";

import { usePlanIntegrations } from "../../../hooks/usePlanIntegrations";

const ACCOUNTING = integrationsByCategory("accounting");

/**
 * Sağlayıcı kaydı: registry'deki her muhasebe entegrasyonu için durum sorgusu +
 * yapılandırma formu. Yeni bir sağlayıcı eklemek için registry'ye bir satır ve
 * buraya bir giriş eklemek yeterlidir — hub kodu değişmez.
 */
const PROVIDERS = {
    bizimhesap: { fetchSettings: fetchBizimhesapSettings, Component: BizimhesapSettingsTab },
    parasut: { fetchSettings: fetchParasutSettings, Component: ParasutSettingsTab },
    mikro: { fetchSettings: fetchMikroSettings, Component: MikroSettingsTab },
};

// Registry sırasını koru; yalnızca formu tanımlı olan sağlayıcıları işle
const PROVIDER_IDS = ACCOUNTING.map((i) => i.id).filter((id) => PROVIDERS[id]);

const EMPTY_STATUS = PROVIDER_IDS.reduce(
    (acc, id) => ({ ...acc, [id]: { configured: false, enabled: false } }),
    {}
);
const ZERO_RELOAD = PROVIDER_IDS.reduce((acc, id) => ({ ...acc, [id]: 0 }), {});

const labelOf = (id) => ACCOUNTING.find((p) => p.id === id)?.name;

const trIncludes = (text, term) =>
    (text || "").toLocaleLowerCase("tr-TR").includes((term || "").toLocaleLowerCase("tr-TR"));

/**
 * Muhasebe entegrasyonu — kart-grid hub. Her sağlayıcı bir kart; "Yapılandır/Bağla"
 * tıklanınca mevcut sağlayıcı formu (ParasutSettingsTab / BizimhesapSettingsTab /
 * MikroSettingsTab) bir modal içinde açılır. Aynı anda yalnızca BİR entegrasyon aktif
 * olabilir (kural backend'de zorlanır; kaydedince diğer kartlar tazelenir).
 */
export default function AccountingSettingsTab({ companyId }) {
    const { has: hasIntegration } = usePlanIntegrations();
    // OAuth dönüşünde (?parasut=...) doğrudan Paraşüt modalını aç
    const cameFromParasut =
        typeof window !== "undefined" && !!new URLSearchParams(window.location.search).get("parasut");

    const [search, setSearch] = useState("");
    const [selectedId, setSelectedId] = useState(cameFromParasut ? "parasut" : null);
    // Diğer formları, bir sağlayıcı kaydedince remount edip güncel duruma çekmek için
    const [reload, setReload] = useState(ZERO_RELOAD);
    const [status, setStatus] = useState(EMPTY_STATUS);

    const loadStatus = useCallback(async () => {
        if (!companyId) return;
        const results = await Promise.all(
            PROVIDER_IDS.map((id) => PROVIDERS[id].fetchSettings(companyId).catch(() => null))
        );
        setStatus(
            PROVIDER_IDS.reduce((acc, id, idx) => {
                const res = results[idx];
                acc[id] = { configured: !!res?.data, enabled: !!res?.data?.enabled };
                return acc;
            }, {})
        );
    }, [companyId]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    // Bir entegrasyon kaydedildiğinde: rozetleri tazele + DİĞER formları remount et
    // (backend diğerlerini otomatik pasifleştirdiği için güncel durumu yüklenmeli) + modalı kapat.
    const handleSaved = (savedId) => {
        loadStatus();
        setReload((r) => {
            const next = { ...r };
            PROVIDER_IDS.filter((id) => id !== savedId).forEach((id) => {
                next[id] = (next[id] ?? 0) + 1;
            });
            return next;
        });
        setSelectedId(null);
    };

    const statusOf = (integration) => {
        // Paket kapsamı dışındaki entegrasyon kilitli çizilir; kaydetmeyi backend
        // zaten 403 ile engelliyor, burada form hiç doldurtulmuyor.
        if (!hasIntegration(integration.planIntegration)) return "locked";
        const st = status[integration.id];
        if (!st) return "passive";
        return st.enabled ? "connected" : "passive";
    };

    const cards = ACCOUNTING.filter((i) => trIncludes(i.name, search));

    const selected = selectedId ? ACCOUNTING.find((i) => i.id === selectedId) : null;
    const selectedConfigured = selectedId ? status[selectedId]?.configured : false;

    // Seçili olan dışında aktif olan sağlayıcı(lar) — tek-aktif kuralı gereği normalde en fazla bir tane
    const otherActiveLabel = selectedId
        ? PROVIDER_IDS.filter((id) => id !== selectedId && status[id]?.enabled)
              .map(labelOf)
              .join(", ") || null
        : null;

    const closeModal = () => setSelectedId(null);

    const SelectedForm = selected ? PROVIDERS[selected.id]?.Component : null;

    return (
        <>
            <p className="text-muted mb-3">
                Cari hareketleriniz seçtiğiniz muhasebe programına otomatik aktarılır. Anlaşmalı
                olduğunuz programı seçip yapılandırın.
            </p>

            <Alert color="info" className="mb-4 shadow-sm" style={{ borderLeft: "4px solid #0dcaf0" }}>
                <i className="bx bx-info-circle me-1" />
                Aynı anda yalnızca <strong>bir</strong> muhasebe entegrasyonu aktif olabilir. Bir
                entegrasyonu aktif edip kaydettiğinizde, diğerleri otomatik olarak pasifleştirilir.
            </Alert>

            <div className="d-flex flex-wrap align-items-center justify-content-end gap-3 mb-4">
                <IntegrationSearch value={search} onChange={setSearch} />
            </div>

            {cards.length === 0 ? (
                <div className="text-center text-muted py-5">
                    <i className="bx bx-search-alt fs-1 d-block mb-2" />
                    Aramanızla eşleşen entegrasyon bulunamadı.
                </div>
            ) : (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                        gap: "1rem",
                    }}
                >
                    {cards.map((integration) => (
                        <IntegrationCard
                            key={integration.id}
                            integration={integration}
                            status={statusOf(integration)}
                            onAction={() => statusOf(integration) === "locked" ? null : setSelectedId(integration.id)}
                        />
                    ))}
                </div>
            )}

            {selected && SelectedForm && (
                <IntegrationConfigModal
                    isOpen
                    toggle={closeModal}
                    integration={selected}
                    title={`${selected.name} ${selectedConfigured ? "Yapılandır" : "Bağla"}`}
                    subtitle="Entegrasyon ayarlarını düzenleyin"
                >
                    <SelectedForm
                        key={`${selected.id}-${reload[selected.id] ?? 0}`}
                        companyId={companyId}
                        otherActiveLabel={otherActiveLabel}
                        onSaved={() => handleSaved(selected.id)}
                    />
                </IntegrationConfigModal>
            )}
        </>
    );
}
